
import { Box } from 'ui/box';
import { Stack } from 'ui/stack';
import { Select } from 'ui/select';

import IconButton from 'components/partials/button/icon-button';

import SpecialAwardEligibilityRow from './special-award-eligibility-row';
import SpecialAwardRegimesSource from './special-award-regimes-source';
import type { RangeOption } from './special-award-regimes-source';
import SpecialAwardStudentDataSource from './special-award-student-data-source';

import Icons from 'assets/icons';

import { SPECIAL_AWARD_CRITERIA_OPTIONS } from '../calculations-step.constants';
import { getAvailableRangesForBlock } from '../hooks/use-special-award-form';
import type { UseSpecialAwardFormResult } from '../hooks/use-special-award-form';
import type { UseSpecialAwardModalDataResult } from '../hooks/use-special-award-modal-data';

import {
  buildRangeKey,
  formatRangeLabel,
} from '../../matrix-step/definition-tab.constants';

import type {
  SpecialAwardCriteria,
  SpecialAwardCriteriaBlock as CriteriaBlock,
} from '../calculations-step.types';

interface CriteriaTypeOption {
  value: SpecialAwardCriteria;
  label: string;
}

export interface SpecialAwardCriteriaBlockProps {
  block: CriteriaBlock;
  form: UseSpecialAwardFormResult;
  data: UseSpecialAwardModalDataResult;
  criteriaOptions?: ReadonlyArray<CriteriaTypeOption>;
}

/** One independent "+ Add criteria" block: its own type select, fields, and delete icon. */
const SpecialAwardCriteriaBlock = ({
  block,
  form,
  data,
  criteriaOptions = SPECIAL_AWARD_CRITERIA_OPTIONS,
}: SpecialAwardCriteriaBlockProps) => {
  const rangeOptions: RangeOption[] = getAvailableRangesForBlock(
    block,
    data.regimes
  ).map((range) => ({
    value: buildRangeKey(range),
    label: formatRangeLabel(range),
  }));

  return (
    <Stack
      data-testid={`criteria-block-${block.blockId}`}
      data-modal-keyboard-ignore
      sx={{ gap: 1.5 }}
    >
      <Stack direction="row" sx={{ alignItems: 'flex-end', gap: 1.5 }}>
        <Box sx={{ minWidth: 0, flex: '1 1 0%' }}>
          <Select<CriteriaTypeOption>
            label="Criteria"
            options={[...criteriaOptions]}
            value={
              criteriaOptions.find((option) => option.value === block.type) ??
              undefined
            }
            onChange={(option) => {
              if (option && !Array.isArray(option)) {
                form.setBlockType(block.blockId, option.value);
              }
            }}
            searchable={false}
            placeholder="Select..."
            data-testid={`criteria-block-type-${block.blockId}`}
          />
        </Box>
        <IconButton
          icon={<Icons.Delete />}
          variant="reserve-text"
          size="md"
          aria-label="Remove criteria"
          onClick={() => form.removeCriteriaBlock(block.blockId)}
          data-testid={`criteria-block-remove-${block.blockId}`}
        />
      </Stack>

      {block.type === 'STUDENT_DATA' && (
        <SpecialAwardStudentDataSource
          blockId={block.blockId}
          category={block.category}
          optionIds={block.optionIds}
          categories={data.customLogicCategories}
          onCategoryChange={(category) =>
            form.setBlockCategory(block.blockId, category)
          }
          onOptionsChange={(optionIds) =>
            form.setBlockOptions(block.blockId, optionIds)
          }
          isLoading={data.isCustomLogicLoading}
        />
      )}

      {block.type === 'AWARDING_REGIMES' && (
        <SpecialAwardRegimesSource
          regimeOptions={data.regimeOptions}
          selectedRegimeIds={block.selectedRegimeIds}
          onRegimesChange={(ids) => form.setBlockRegimeIds(block.blockId, ids)}
          rangeOptions={rangeOptions}
          selectedRangeKeys={block.selectedRangeKeys}
          onRangesChange={(keys) => form.setBlockRangeKeys(block.blockId, keys)}
          rangeLabel={data.rangeLabel}
          rankOptions={data.rankOptions}
          selectedRankIds={block.selectedRankIds}
          onRanksChange={(ids) => form.setBlockRankIds(block.blockId, ids)}
          isRanksLoading={data.isRanksLoading}
        />
      )}

      {block.type === 'ELIGIBILITY' && (
        <SpecialAwardEligibilityRow
          row={{
            rowId: block.blockId,
            criteria: block.criteria,
            operand: block.operand,
            amount: block.amount,
            amountFrom: block.amountFrom,
            amountTo: block.amountTo,
            dateFrom: block.dateFrom,
            dateTo: block.dateTo,
            ceebCodes: block.ceebCodes,
          }}
          criteriaOptions={data.eligibilityCriteriaOptions}
          operandOptions={data.eligibilityOperandOptions}
          showDelete={false}
          onCriteriaChange={(_blockId, criteria) =>
            form.setBlockEligibilityCriteria(block.blockId, criteria)
          }
          onOperandChange={(_blockId, operand) =>
            form.setBlockOperand(block.blockId, operand)
          }
          onValueChange={(_blockId, field, value) =>
            form.setBlockValue(block.blockId, field, value)
          }
          onCeebCodesChange={(_blockId, updater) =>
            form.setBlockCeebCodes(block.blockId, updater)
          }
        />
      )}
    </Stack>
  );
};

export default SpecialAwardCriteriaBlock;
